import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { BehaviorSubject, Observable, retry, throwError, firstValueFrom } from 'rxjs';

import {
  API_ENDPOINT,
  ERROR_MESSAGE,
  LOCAL_STORAGE_KEY,
  ROUTER_CONFIG,
  Role,
  SESSION_STORAGE_KEY,
  TRIGGER_SESSION_KEY,
} from '@careconne/core/configs';
import { UserType } from '@careconne/core/models/enums';
import { ApiResponse, PassLoginModel } from '@careconne/core/models/interfaces';
import {
  ApiService,
  AutoLogoutService,
  HttpCancelService,
  LocalStorageService,
  RequestService,
  SessionStorageService,
} from '@careconne/core/services/common';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private hasQueryTokenSubject = new BehaviorSubject<boolean>(false);
  hasQueryToken = this.hasQueryTokenSubject.asObservable();

  constructor(
    private readonly http: ApiService,
    private router: Router,
    private autoLogoutService: AutoLogoutService,
    private cancelService: HttpCancelService,
    private requestService: RequestService,
    private sessionStorageService: SessionStorageService,
    private localStorageService: LocalStorageService,
    private dialog: MatDialog,
    private jwtHelperService: JwtHelperService,
  ) {}

  get type(): UserType | null {
    const userSession = this.localStorageService.getUserSession();
    if (userSession && userSession.accessToken) {
      const decode = this.jwtHelperService.decodeToken(userSession.accessToken);
      return decode.type;
    }
    return null;
  }

  get isAccessTokenExpired(): boolean {
    const accessToken = this.localStorageService.getAccessToken();
    if (accessToken) {
      return this.jwtHelperService.isTokenExpired(accessToken);
    }
    return true;
  }

  get isRefreshTokenExpired(): boolean {
    const refreshToken = this.localStorageService.getRefreshToken();
    if (refreshToken) {
      return this.jwtHelperService.isTokenExpired(refreshToken);
    }
    return true;
  }

  setHasQueryToken(val: boolean) {
    this.hasQueryTokenSubject.next(val);
  }

  loggedIn() {
    if (this.isRefreshTokenExpired) {
      if (this.localStorageService.getUserSession()) {
        this.logout();
      }
      return false;
    } else {
      return true;
    }
  }

  getModuleByToken(token: string): string {
    const decode = this.jwtHelperService.decodeToken(token);
    return decode.module;
  }

  requestAccessToken(refreshToken: string): Observable<ApiResponse<any>> {
    if (refreshToken) {
      return this.http.post(API_ENDPOINT.token.refreshToken, {
        refreshToken,
      });
    } else {
      return throwError(() => new Error(ERROR_MESSAGE.refreshTokenNotFound));
    }
  }

  getRoles(token: string): Role | null {
    const decoded = this.jwtHelperService.decodeToken(token);
    if (!decoded) {
      return null;
    }
    if (decoded.type === 'CK_PASS' || decoded.userType === 'CK_PASS') {
      return Role.roleCCJAdmin;
    } else if (decoded.userId) {
      return Role.roleCCJAdmin;
    }
    return null;
  }

  async loginByCKPass(loginData: PassLoginModel) {
    const res = await firstValueFrom(this.http.post(API_ENDPOINT.auth.loginByCKPass, loginData));
    if (res && res.result) {
      const userSession = {
        accessToken: res.result.accessToken,
        refreshToken: res.result.refreshToken,
      };
      this.localStorageService.setUserSession(userSession);
      this.sessionStorageService.setItem(SESSION_STORAGE_KEY.sessionLogin, SESSION_STORAGE_KEY.sessionLogin);
      this.localStorageService.trigger(TRIGGER_SESSION_KEY.setToken);
      this.localStorageService.setItem(LOCAL_STORAGE_KEY.userId, res.result.userId);

      const fullName = res.result.lastName + res.result.firstName;
      this.localStorageService.setItem(LOCAL_STORAGE_KEY.fullName, fullName);

      this.router.navigate([ROUTER_CONFIG.home]);
    }
  }

  async loginByToken(params: { accessToken: string; refreshToken: string }): Promise<any> {
    const decodeToken = this.jwtHelperService.decodeToken(params.accessToken);
    if (!decodeToken || !decodeToken.userId) {
      throw new Error('Invalid token!');
    }

    const isEqualRefreshToken = params.refreshToken === this.localStorageService.getRefreshToken();
    if (isEqualRefreshToken) {
      this.localStorageService.setUserSession(params);
      this.router.navigate([ROUTER_CONFIG.download]);
      return;
    }

    await this.logout(true);

    // Set token to call api get user
    this.localStorageService.setUserSession(params);

    const res = await firstValueFrom(this.http.get(API_ENDPOINT.auth.getUserByToken));
    if (res && res.result) {
      this.sessionStorageService.setItem(SESSION_STORAGE_KEY.sessionLogin, SESSION_STORAGE_KEY.sessionLogin);
      this.localStorageService.setItem(LOCAL_STORAGE_KEY.userId, decodeToken.userId);
      this.localStorageService.setItem(LOCAL_STORAGE_KEY.fullName, res.result.fullName);
      this.localStorageService.setItem(LOCAL_STORAGE_KEY.roleList, res.result.roleList);
      this.router.navigate([ROUTER_CONFIG.download]);
    } else {
      throw new Error('ERR_USER_NOT_FOUND');
    }
  }

  async logout(withoutNavigateToLogin = false) {
    if (!this.requestService.checkCompletedRequests()) {
      this.cancelService.cancelPendingRequest();
    }
    if (!this.isAccessTokenExpired || !this.isRefreshTokenExpired) {
      await firstValueFrom(this.callLogout().pipe(retry(1)));
    }
    this.autoLogoutService.stop();
    this.localStorageService.clear();
    this.sessionStorageService.clear();
    if (!withoutNavigateToLogin) {
      this.sessionStorageService.removeItems([SESSION_STORAGE_KEY.sessionLogin]);
      this.localStorageService.trigger(TRIGGER_SESSION_KEY.removeToken);
      await this.router.navigate([ROUTER_CONFIG.auth.login]);
      this.dialog.closeAll();
    }
  }

  verifyRefreshToken(): Observable<any> {
    const refreshToken = this.localStorageService.getRefreshToken();
    if (refreshToken) {
      return this.http.post(API_ENDPOINT.auth.verifyRefreshToken, {
        refreshToken,
      });
    } else {
      console.error(ERROR_MESSAGE.refreshTokenNotFound);
      return throwError(() => new Error(ERROR_MESSAGE.refreshTokenNotFound));
    }
  }

  private callLogout() {
    return this.http.delete(API_ENDPOINT.token.logout);
  }

  clearForErrPages() {
    this.localStorageService.clear();
    this.sessionStorageService.clear();
  }
}
